import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444", "#EC4899", "#14B8A6", "#F97316"];

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#2563EB", icon: "", sort_order: 0 });

  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", color: COLORS[categories.length % COLORS.length], icon: "", sort_order: categories.length + 1 });
    setShowDialog(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, color: cat.color || "#2563EB", icon: cat.icon || "", sort_order: cat.sort_order || 0 });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, sort_order: parseInt(form.sort_order) || 0 };
      if (editing) {
        await api.put(`/categories/${editing.category_id}`, payload);
        toast.success("Category updated");
      } else {
        await api.post("/categories", payload);
        toast.success("Category created");
      }
      setShowDialog(false);
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Deleted"); fetchCategories(); }
    catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-8" data-testid="categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="categories-title">Categories</h1>
          <p className="text-sm text-slate-500 mt-1">{categories.length} categories</p>
        </div>
        <Button onClick={openCreate} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-category-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Category
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="categories-grid">
        {categories.map((cat) => (
          <div key={cat.category_id} className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-200" data-testid={`category-card-${cat.category_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: cat.color }}>
                {cat.name.charAt(0)}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(cat)} data-testid={`edit-category-${cat.category_id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(cat.category_id)} data-testid={`delete-category-${cat.category_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
              </div>
            </div>
            <h3 className="font-medium text-slate-900">{cat.name}</h3>
            <p className="text-xs text-slate-400 mt-1">Sort order: {cat.sort_order}</p>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Category" : "New Category"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">{editing ? "Update category details" : "Create a new product category"}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Color</Label>
              <div className="flex gap-2 mt-1">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })} className={`h-8 w-8 rounded-full border-2 transition-transform ${form.color === c ? "border-slate-900 scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Sort Order</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="category-sort-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="category-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="category-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
